import React from 'react';
import { Chip, Divider, Grid, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AccountData, PassOrderPageData } from '../../api';
import { useLanguage } from '../../utils/GeneralUtils';
import { customerFacingPriceFormatter } from '../../orderPage/utils';
import { withBookingFee } from '../../orderPage/core/pricing';
import { isRecurringMonthly } from '../utils/passUtils';

type Props = {
  passOrderPage: PassOrderPageData;
  account: AccountData;
  isLastPurchased?: boolean;
};

export const BuyPassCard = ({ passOrderPage, account, isLastPurchased }: Props) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const navigate = useNavigate();

  const redeemableEntries = (passOrderPage.redeemable_on || [])
    .map((item) => {
      const op = account.experiences?.[0]?.order_pages?.find((p: any) => p.id === item.order_page_id);
      const name = op?.label?.[language] || '';
      return name ? { name, limit: item.limit } : null;
    })
    .filter(Boolean) as { name: string; limit?: number | null }[];

  return (
    <Paper
      elevation={0}
      onClick={() => navigate(`/passes/buy/${passOrderPage.id}`)}
      sx={{
        borderRadius: 2,
        p: 2,
        cursor: 'pointer',
        border: '2px solid',
        borderColor: isLastPurchased ? 'primary.main' : 'divider',
        '&:hover': { borderColor: 'primary.main' },
      }}>
      <Grid container flexDirection="column" gap={1.5}>
        <Grid container alignItems="center" justifyContent="space-between">
          <Typography variant="h6" fontWeight={600}>
            {passOrderPage.label?.[language] || t('manage_passes.pass.unnamed')}
          </Typography>
          {isLastPurchased && (
            <Chip
              label={t('manage_passes.buy.lastPurchased')}
              size="small"
              color="primary"
              variant="filled"
              sx={{ fontWeight: 500 }}
            />
          )}
        </Grid>

        <Grid container gap={1}>
          <Grid
            item
            sx={{
              flex: 1,
              textAlign: 'center',
              py: 1.5,
              px: 1,
              borderRadius: 1.5,
              backgroundColor: 'action.hover',
            }}>
            <Typography variant="caption" color="text.secondary" display="block">
              {t('pass.num_punches')}
            </Typography>
            <Typography variant="subtitle1" fontWeight={600}>
              {passOrderPage.num_punches ?? t('pass.unlimited')}
            </Typography>
          </Grid>
          <Grid
            item
            sx={{
              flex: 1,
              textAlign: 'center',
              py: 1.5,
              px: 1,
              borderRadius: 1.5,
              backgroundColor: 'action.hover',
            }}>
            <Typography variant="caption" color="text.secondary" display="block">
              {t('pass.validity_days')}
            </Typography>
            <Typography variant="subtitle1" fontWeight={600}>
              {t('pass.days', { count: passOrderPage.validity_days })}
            </Typography>
          </Grid>
        </Grid>

        <Divider />

        <Grid container justifyContent="space-between" alignItems="center">
          <Typography variant="body2" color="text.secondary">
            {redeemableEntries.length > 0
              ? `${t('manage_passes.pass.redeemableOn')} ${redeemableEntries
                  .map(({ name, limit }) =>
                    limit != null ? `${name} ${t('pass.punch_limit', { count: limit })}` : name,
                  )
                  .join(', ')}`
              : t('manage_passes.pass.redeemableOnAll')}
          </Typography>
          <Typography variant="h6" fontWeight={700} color="primary">
            {customerFacingPriceFormatter(withBookingFee(passOrderPage.price, account), account)}
            {isRecurringMonthly(passOrderPage.pass_type) && ` ${t('manage_passes.buy.perMonth')}`}
          </Typography>
        </Grid>
      </Grid>
    </Paper>
  );
};
